import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getAvatarUrl } from "src/Common/common";
import { handleError } from "src/Common/function";
import PolicyService from "src/Services/API/policy.service";
import PermissionService from "src/Services/API/permission.service";
import RoleService from "src/Services/API/role.service";
import UserService from "src/Services/API/user.service";

const POLICY_TYPES = ["system", "operational", "integration", "custom", "restricted"];

const STEPS = [
  { id: 1, label: "Details" },
  { id: 2, label: "Permissions" },
  { id: 3, label: "Assign" },
];

const MAX_VISIBLE_USER_CHIPS = 6;

// Real avatar image when available; falls back to an initials circle on missing
// or broken image (mirrors the Avatar pattern in Setting/Team/status/Status).
function UserAvatar({ user, className = "w-5 h-5 text-[10px]" }) {
  const [errored, setErrored] = useState(false);
  if (user?.avatar && !errored) {
    return (
      <img
        src={getAvatarUrl(user.avatar)}
        onError={() => setErrored(true)}
        alt={user?.name ?? ""}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    );
  }
  const initial = (user?.name || user?.email || "?")[0].toUpperCase();
  return (
    <span
      className={`shrink-0 rounded-full bg-[#EAF3FF] text-[#0058E5] font-OpenSansSemiBold flex items-center justify-center ${className}`}
    >
      {initial}
    </span>
  );
}

/**
 * Create / Edit Policy — a 3-step wizard modal.
 *
 *   Step 1 (Details):     name, description, type.
 *   Step 2 (Permissions): permissions grouped by resource; per-resource
 *                         "All … Permissions" wildcard (current + future) or
 *                         individual checkboxes. Checked = allow (no deny UI).
 *   Step 3 (Assign):      attach the policy to roles and/or directly to users.
 *
 * `policyId` null → create; set → edit (all steps prefilled).
 */
function PolicyWizardModal({ shown, policyId, onClose, onSaved }) {
  const isEdit = Boolean(policyId);
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  // Step 1
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("custom");

  // Step 2 — catalog + selection: { [resource]: { wildcard, keys: [] } }
  const [resources, setResources] = useState([]);
  const [selection, setSelection] = useState({});
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");

  // Step 3
  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showAllChips, setShowAllChips] = useState(false);

  // Load catalog + roles + users once per open; prefill when editing.
  useEffect(() => {
    if (!shown) return;
    setStep(1);
    setIsSaving(false);
    setName("");
    setDescription("");
    setType("custom");
    setSelection({});
    setSearch("");
    setSelectedRoleIds([]);
    setSelectedUserIds([]);
    setUserSearch("");
    setShowAllChips(false);

    PermissionService.getCatalog()
      .then((res) => {
        const list = res.data?.resources ?? [];
        setResources(list);
        // All resource sections start collapsed; the user expands what they need
        // (a search still auto-expands matching sections — see `isOpen` below).
        setExpanded({});
      })
      .catch(handleError);
    // The assign step only needs role id/name and user display fields, so use
    // the auth-only role list and the ungated user picker endpoint — the
    // manage list (iam:readRole) and full user listing (user:read) would 403
    // policy managers without those permissions.
    RoleService.getAll()
      .then((res) => setRoles(res.data ?? []))
      .catch(handleError);
    UserService.getUsersForAssign(null)
      .then((res) => setUsers(res.data ?? []))
      .catch(handleError);

    if (policyId) {
      PolicyService.getOne(policyId)
        .then((res) => {
          const p = res.data;
          setName(p.name ?? "");
          setDescription(p.description ?? "");
          setType(p.type ?? "custom");

          const sel = {};
          (p.permissions ?? []).forEach((perm) => {
            const r = perm.resource;
            if (!r) return;
            sel[r] = sel[r] || { wildcard: false, keys: [] };
            if (perm.wildcard || perm.action === "*") sel[r].wildcard = true;
            else sel[r].keys.push(perm.key);
          });
          setSelection(sel);
          setSelectedRoleIds((p.roles ?? []).map((r) => r.id));
          setSelectedUserIds((p.users ?? []).map((u) => u.id));
        })
        .catch(handleError);
    }
  }, [shown, policyId]);

  // Search filters permissions across sections (matching sections auto-expand).
  const visibleResources = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return resources;
    return resources
      .map((r) => ({
        ...r,
        permissions: (r.permissions ?? []).filter(
          (p) =>
            p.key?.toLowerCase().includes(q) ||
            p.displayName?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q) ||
            r.label?.toLowerCase().includes(q),
        ),
      }))
      .filter((r) => r.permissions.length > 0);
  }, [resources, search]);

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedUserIds.includes(u.id)),
    [users, selectedUserIds],
  );

  const userMatches = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter((u) => !selectedUserIds.includes(u.id))
      .filter(
        (u) =>
          u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [users, userSearch, selectedUserIds]);

  if (!shown) return null;

  const patchResource = (resource, patch) =>
    setSelection((prev) => ({
      ...prev,
      [resource]: { wildcard: false, keys: [], ...prev[resource], ...patch },
    }));

  const toggleWildcard = (resource) => {
    const cur = selection[resource];
    patchResource(resource, { wildcard: !cur?.wildcard });
  };

  const toggleKey = (resource, key) => {
    const cur = selection[resource] ?? { wildcard: false, keys: [] };
    const has = cur.keys.includes(key);
    patchResource(resource, {
      keys: has ? cur.keys.filter((k) => k !== key) : [...cur.keys, key],
    });
  };

  const toggleRole = (roleId) =>
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );

  const addUser = (userId) => {
    setSelectedUserIds((prev) => [...prev, userId]);
    setUserSearch("");
  };

  const removeUser = (userId) =>
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));

  const next = () => {
    if (step === 1 && !name.trim()) {
      toast("Policy name is required");
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  const save = () => {
    const permissions = [];
    Object.entries(selection).forEach(([resource, s]) => {
      if (s.wildcard) permissions.push({ permissionKey: `${resource}:*`, effect: "allow" });
      else s.keys.forEach((key) => permissions.push({ permissionKey: key, effect: "allow" }));
    });

    const payload = { name: name.trim(), description, type, permissions };
    setIsSaving(true);
    const req = isEdit
      ? PolicyService.update(policyId, payload).then(() => policyId)
      : PolicyService.create(payload).then((res) => res.data?.id);

    req
      .then(async (id) => {
        if (id) {
          await PolicyService.assignToRoles(id, selectedRoleIds);
          await PolicyService.assignToUsers(id, selectedUserIds);
        }
        toast(isEdit ? "Policy updated" : "Policy created");
        onSaved?.(id);
        onClose?.();
      })
      .catch(handleError)
      .finally(() => setIsSaving(false));
  };

  const sectionCount = (r) => {
    const s = selection[r.resource];
    if (!s) return null;
    if (s.wildcard) return "All";
    if (!s.keys.length) return null;
    return `${s.keys.length}/${(r.permissions ?? []).length} Selected`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-[12px] w-[784px] max-w-[95vw] max-h-[92vh] flex flex-col shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#EEF0F9]">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <h2 className="text-[20px] leading-7 font-OpenSansSemiBold text-[#1A2332] capitalize">
                {isEdit ? "Edit Policy" : "Create Policy"}
              </h2>
              <p className="text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#7C829A]">
                Create a reusable permission bundle.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#868DA6] hover:bg-[#F0F1F5] hover:text-[#1A2332]"
              title="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M12 4L4 12M4 4l8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-4 px-12 py-4 bg-[#FBFCFF] border-b border-[#EEF0F9]">
          {STEPS.map((s, i) => (
            <Fragment key={s.id}>
              {i > 0 && (
                <div className={`flex-1 h-px ${step >= s.id ? "bg-[#0065FD]" : "bg-[#E5E8F3]"}`} />
              )}
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold ${
                    step > s.id
                      ? "bg-[#0065FD] text-white"
                      : step === s.id
                      ? "bg-[#0065FD] text-white shadow-[0px_0px_0px_4px_#DBEAFE]"
                      : "text-[#868DA6] outline outline-[1.6px] -outline-offset-[1.6px] outline-[#E5E8F3]"
                  }`}
                >
                  {step > s.id ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    s.id
                  )}
                </span>
                <span
                  className={`text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold ${
                    step >= s.id ? "text-[#0065FD]" : "text-[#868DA6]"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            </Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="px-8 py-8 overflow-y-auto flex-1">
          {/* ---- Step 1: Details ---- */}
          {step === 1 && (
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-1">
                <label className="text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                  Policy Name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Field Technician Standard"
                  className="w-full h-10 px-3 rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] placeholder:text-[#868DA6] focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-[#E5E8F3]"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this policy allows"
                  rows={3}
                  className="w-full h-24 p-3 rounded-[6px] bg-[#FBFCFF] outline outline-1 -outline-offset-1 outline-[#E5E8F3] text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] placeholder:text-[#868DA6] focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-[#E5E8F3] resize-none"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                  Type
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full h-10 px-3 rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-[#E5E8F3] capitalize"
                >
                  {POLICY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* ---- Step 2: Permissions ---- */}
          {step === 2 && (
            <div className="flex flex-col gap-8">
              <div className="relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#68778D]"
                >
                  <path
                    d="M7.333 12.667A5.333 5.333 0 107.333 2a5.333 5.333 0 000 10.667zM14 14l-2.9-2.9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search permissions (e.g. 'delete', 'work orders')…"
                  className="w-full h-10 pl-9 pr-3 rounded-[8px] outline outline-1 -outline-offset-1 outline-[#C9CEE2] text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] placeholder:text-[#868DA6] focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-[#C9CEE2]"
                />
              </div>

              {visibleResources.length === 0 ? (
                <p className="text-[13px] font-OpenSansRegular text-[#868DA6] py-8 text-center">
                  No permissions match your search.
                </p>
              ) : (
                <div className="flex flex-col gap-4">
                  {visibleResources.map((r) => {
                    const sel = selection[r.resource] ?? { wildcard: false, keys: [] };
                    const isOpen = Boolean(search.trim()) || expanded[r.resource];
                    const badge = sectionCount(r);
                    return (
                      <div
                        key={r.resource}
                        className="bg-white rounded-[8px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] overflow-hidden"
                      >
                        {/* Section header */}
                        <button
                          onClick={() =>
                            setExpanded((prev) => ({
                              ...prev,
                              [r.resource]: !isOpen,
                            }))
                          }
                          className={`w-full flex items-center justify-between px-4 py-3 bg-[#F8F9FE] ${
                            isOpen ? "border-b border-[#E5E8F3]" : ""
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-[16px] leading-6 capitalize font-OpenSansSemiBold text-[#1A2332]">
                              {r.label}
                            </span>
                            {badge && (
                              <span
                                className={`inline-flex px-2 py-[2px] rounded-full text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold ${
                                  sel.wildcard
                                    ? "bg-[#E5EFFF] text-[#0058E5]"
                                    : "bg-[#EEF0F9] text-[#5F6477]"
                                }`}
                              >
                                {badge}
                              </span>
                            )}
                          </span>
                          <span className="p-1 text-[#868DA6]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                              className={`transition-transform ${isOpen ? "" : "-rotate-90"}`}
                            >
                              <path
                                d="M4.5 6.75L9 11.25l4.5-4.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </button>

                        <div
                          className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                            isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                          }`}
                        >
                          <div className="overflow-hidden">
                            <div className="p-4 flex flex-col gap-6">
                              {/* Wildcard: all current + future permissions */}
                              <label className="flex items-start gap-3 p-3 rounded-[8px] bg-[#F8F9FE] outline outline-1 -outline-offset-1 outline-[#E5E8F3] cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={sel.wildcard}
                                  onChange={() => toggleWildcard(r.resource)}
                                  className="accent-[#0065FD] w-4 h-4 mt-[2px]"
                                />
                                <span>
                                  <span className="block text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                                    All {r.label} Permissions
                                  </span>
                                  <span className="block text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#7C829A]">
                                    Includes current and future {r.label} permissions.
                                  </span>
                                </span>
                              </label>

                              <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                                {(r.permissions ?? []).map((p) => (
                                  <label
                                    key={p.key}
                                    className={`flex items-start gap-3 ${
                                      sel.wildcard
                                        ? "opacity-45 cursor-not-allowed"
                                        : "cursor-pointer"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={sel.wildcard}
                                      checked={sel.wildcard || sel.keys.includes(p.key)}
                                      onChange={() => toggleKey(r.resource, p.key)}
                                      className="accent-[#0065FD] w-4 h-4 mt-[2px]"
                                    />
                                    <span>
                                      <span className="block text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                                        {p.displayName}
                                      </span>
                                      <span className="block text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#7C829A]">
                                        {p.description || p.key}
                                      </span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ---- Step 3: Assign ---- */}
          {step === 3 && (
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <p className="text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                  Assign To Roles
                </p>
                <div className="flex flex-wrap gap-3">
                  {roles.map((r) => {
                    const checked = selectedRoleIds.includes(r.id);
                    return (
                      <label
                        key={r.id}
                        className={`flex-1 min-w-[260px] flex items-center gap-3 px-4 py-3 rounded-[8px] outline outline-1 -outline-offset-1 cursor-pointer ${
                          checked ? "outline-[#0065FD] bg-[#F5F9FF]" : "outline-[#E5E8F3] hover:outline-[#B9C4E0]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRole(r.id)}
                          className="accent-[#0065FD] w-4 h-4"
                        />
                        <span
                          className={`text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold capitalize ${
                            checked ? "text-[#0065FD]" : "text-[#1A2332]"
                          }`}
                        >
                          {r.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <p className="flex items-center gap-2 text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                  Assign To Users
                  <span className="text-[#868DA6] font-OpenSansRegular normal-case">(Optional)</span>
                </p>
                <div className="relative min-h-[46px] rounded-[8px] outline outline-1 -outline-offset-1 outline-[#E5E7EB] px-3 py-2 flex flex-wrap items-center gap-2">
                  {(showAllChips
                    ? selectedUsers
                    : selectedUsers.slice(0, MAX_VISIBLE_USER_CHIPS)
                  ).map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-1.5 pl-1 pr-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold text-[#5F6477]"
                    >
                      <UserAvatar user={u} />
                      {u.name}
                      <button
                        onClick={() => removeUser(u.id)}
                        className="text-[#868DA6] hover:text-[#D93A3A] flex items-center"
                        title="Remove"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M9 3L3 9M3 3l6 6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {!showAllChips && selectedUsers.length > MAX_VISIBLE_USER_CHIPS && (
                    <button
                      onClick={() => setShowAllChips(true)}
                      className="inline-flex px-2.5 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold text-[#5F6477] hover:bg-[#E5E8F3]"
                    >
                      +{selectedUsers.length - MAX_VISIBLE_USER_CHIPS} more
                    </button>
                  )}
                  <input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder={selectedUsers.length ? "Search users…" : "Search users to assign…"}
                    className="flex-1 min-w-[140px] py-1 text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] placeholder:text-[#868DA6] outline-none bg-transparent"
                  />
                  {userMatches.length > 0 && (
                    <div className="absolute left-0 right-0 bottom-full mb-1 z-[70] bg-white outline outline-1 -outline-offset-1 outline-[#E5E8F3] rounded-[8px] shadow-[0px_8px_24px_rgba(16,24,40,0.12)] max-h-[220px] overflow-y-auto">
                      {userMatches.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => addUser(u.id)}
                          className="w-full flex items-center gap-2.5 text-left px-3 py-2 hover:bg-[#F8F9FE]"
                        >
                          <UserAvatar user={u} className="w-7 h-7 text-[12px]" />
                          <span className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332]">
                            {u.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#868DA6]">
                  Users receive this policy directly, in addition to any policies from their roles.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E5E8F3] flex items-center justify-end gap-6">
          {step === 1 ? (
            <button
              onClick={onClose}
              className="h-10 px-1 rounded-[6px] text-[16px] leading-6 capitalize font-OpenSansSemiBold text-[#7C829A] hover:text-[#1A2332]"
            >
              Cancel
            </button>
          ) : (
            <button
              onClick={back}
              className="h-10 px-1 rounded-[6px] inline-flex items-center gap-2 text-[16px] leading-6 capitalize font-OpenSansSemiBold text-[#7C829A] hover:text-[#1A2332]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12L6 8l4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back
            </button>
          )}

          {step < 3 ? (
            <button
              onClick={next}
              className="h-10 px-4 rounded-[8px] bg-[#0065FD] text-white text-[16px] leading-6 capitalize font-OpenSansSemiBold hover:bg-[#0048bd]"
            >
              Next
            </button>
          ) : (
            <button
              onClick={save}
              disabled={isSaving}
              className="h-10 px-4 rounded-[8px] bg-[#0065FD] text-white text-[16px] leading-6 capitalize font-OpenSansSemiBold hover:bg-[#0048bd] disabled:opacity-60"
            >
              {isSaving ? "Saving…" : isEdit ? "Save Changes" : "Create Policy"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PolicyWizardModal;
